export const DOSSIERMEDICAL_LESSONS = [
  {
    id: 'dossiermedical-1',
    numero: 1,
    titre: "Comprendre votre dossier médical",
    sousTitre: "Vos droits sur vos données de santé",
    contexte: "Vous avez le droit d'accéder à toutes vos informations médicales. Mais où sont-elles et comment y accéder ?",
    explication: [
      "**Le dossier médical** regroupe toutes vos informations de santé : antécédents, traitements, résultats d'analyses, comptes rendus d'hospitalisation.",
      "**Votre médecin traitant** conserve votre dossier. Vous avez le droit de le consulter et d'en demander une copie — c'est la loi (article L1111-7 du Code de la santé publique).",
      "**Le Dossier Médical Partagé (DMP)** est votre carnet de santé numérique sur Mon Espace Santé (monespacesante.fr). Il regroupe vos documents médicaux accessibles en ligne.",
      "**Comment demander votre dossier** : formulez une demande écrite à votre médecin ou à l'hôpital. Ils ont 8 jours ouvrables pour vous répondre (2 mois pour les dossiers anciens).",
      "**C'est gratuit** sauf si vous demandez des copies papier — dans ce cas les frais de reproduction sont à votre charge.",
    ],
    question: "Vous demandez votre dossier médical à votre médecin. Dans quel délai doit-il vous répondre ?",
    options: [
      { id: 'a', text: "8 jours ouvrables maximum", correct: true },
      { id: 'b', text: "3 mois", correct: false },
      { id: 'c', text: "Le médecin n'est pas obligé de vous donner votre dossier", correct: false },
    ],
    explication_reponse: "La loi impose un délai de 8 jours ouvrables pour les dossiers récents (moins de 5 ans). C'est votre droit fondamental — aucun médecin ne peut vous refuser l'accès à vos propres données de santé.",
  },
  {
    id: 'dossiermedical-2',
    numero: 2,
    titre: "Mon Espace Santé",
    sousTitre: "Votre carnet de santé numérique",
    contexte: "Mon Espace Santé (monespacesante.fr) est le nouveau portail officiel qui centralise toutes vos informations de santé en ligne.",
    explication: [
      "**Mon Espace Santé** est accessible sur monespacesante.fr avec votre numéro de sécurité sociale et un code reçu par courrier ou SMS.",
      "**Ce que vous y trouvez** : vos ordonnances, résultats d'analyses, comptes rendus médicaux, vaccinations, allergies connues.",
      "**Vous contrôlez tout** : vous choisissez quels professionnels de santé peuvent accéder à votre dossier, et vous pouvez masquer certains documents.",
      "**L'appli mobile** Mon Espace Santé permet d'avoir votre carnet de santé toujours avec vous — très pratique aux urgences ou chez un nouveau médecin.",
      "**Votre médecin peut y déposer** des documents directement — vous les recevez en notification. Plus besoin de conserver des dizaines de papiers.",
    ],
    question: "Vous allez aux urgences. Comment Mon Espace Santé peut-il vous aider ?",
    options: [
      { id: 'a', text: "Le médecin urgentiste peut accéder à vos antécédents et traitements en cours", correct: true },
      { id: 'b', text: "Ça ne sert à rien aux urgences, c'est réservé au médecin traitant", correct: false },
      { id: 'c', text: "L'application remplace la carte Vitale aux urgences", correct: false },
    ],
    explication_reponse: "Aux urgences, donner accès à Mon Espace Santé permet au médecin de voir immédiatement vos allergies, médicaments en cours et antécédents — cela évite les erreurs médicales et accélère la prise en charge. C'est souvent vital.",
  },
  {
    id: 'dossiermedical-3',
    numero: 3,
    titre: "Organiser ses documents de santé",
    sousTitre: "Ne plus rien perdre",
    contexte: "Ordonnances éparpillées, résultats d'analyses introuvables, carnet de vaccination disparu... Comment s'organiser efficacement ?",
    explication: [
      "**Créez un dossier physique** avec des intercalaires : Ordonnances en cours, Résultats d'analyses, Comptes rendus, Vaccinations, Courriers hospitaliers.",
      "**Photographiez chaque document** dès réception avec votre téléphone et classez-les dans un dossier 'Santé' sur votre cloud (Google Drive, iCloud).",
      "**Les ordonnances en cours** : gardez-les dans votre portefeuille ou sac — elles peuvent être demandées à la pharmacie ou lors d'une consultation.",
      "**Le carnet de vaccination** : conservez-le précieusement et emportez-le à chaque consultation. En cas de perte, votre médecin peut reconstituer votre historique.",
      "**Documents à toujours avoir** : carte Vitale, attestation mutuelle, liste de vos médicaments et allergies (une fiche simple suffit).",
    ],
    question: "Vous perdez votre carnet de vaccination. Que faire ?",
    options: [
      { id: 'a', text: "Contacter votre médecin traitant — il peut reconstituer votre historique vaccinal", correct: true },
      { id: 'b', text: "Refaire toutes les vaccinations depuis le début", correct: false },
      { id: 'c', text: "Rien — le carnet de vaccination n'est pas indispensable", correct: false },
    ],
    explication_reponse: "Votre médecin traitant conserve votre historique vaccinal dans votre dossier. Il peut reconstituer un nouveau carnet. Certaines vaccinations sont aussi visibles dans Mon Espace Santé si elles ont été enregistrées numériquement.",
  },
  {
    id: 'dossiermedical-4',
    numero: 4,
    titre: "Comprendre une ordonnance",
    sousTitre: "Lire et utiliser correctement",
    contexte: "Votre médecin vous remet une ordonnance avec des abréviations et des instructions que vous ne comprenez pas toujours.",
    explication: [
      "**Les éléments obligatoires** : nom du médecin, date, votre nom, le médicament avec dosage, la posologie (comment le prendre) et la durée du traitement.",
      "**Les abréviations courantes** : 1cp = 1 comprimé, matin/midi/soir, 'à jeun' = avant de manger, 'pendant les repas' = avec de la nourriture.",
      "**Durée de validité** : 3 mois pour la plupart des médicaments, 1 an pour certains traitements chroniques. Après ce délai, elle n'est plus acceptée en pharmacie.",
      "**En cas de doute** : le pharmacien est votre allié. Il peut expliquer chaque médicament, ses effets secondaires et les interactions avec d'autres traitements.",
      "**Gardez une copie** de vos ordonnances de traitements chroniques — si vous perdez l'original avant d'aller à la pharmacie, la copie photo sur votre téléphone peut suffire.",
    ],
    question: "Votre ordonnance date de 4 mois. Est-elle encore valable à la pharmacie ?",
    options: [
      { id: 'a', text: "Non — la plupart des ordonnances sont valables 3 mois maximum", correct: true },
      { id: 'b', text: "Oui — une ordonnance est valable 1 an", correct: false },
      { id: 'c', text: "Ça dépend du pharmacien", correct: false },
    ],
    explication_reponse: "La validité standard d'une ordonnance est de 3 mois. Après ce délai, vous devrez reconsulter votre médecin pour renouveler la prescription. Seuls certains traitements chroniques (hypertension, diabète...) ont une validité d'un an.",
  },
];
